/* global React, Icon, Button, Modal, EmptyState, Field, toast, ReceiptInboxExpanded, ReceiptPaymentSelect, receiptDuplicateUploadText, receiptUiName */
// Bokforing av flere bilag fra innboksen.
//
// To flater, samme problem: bulk-bokforing skjer i blinde. Dialogen viser hva
// som faktisk kommer til aa bli bokfort for noe skrives, og hva som ble hoppet
// over etterpaa. Gjennomgangen tar bilagene ett om gangen for den som heller vil
// se dokumentet enn aa stole paa analysen.
//
// Ligger utenfor purchases.jsx med vilje: den fila er stor nok fra for.

// Rekkefolgen er ikke tilfeldig - det som sperrer staar over det som bare
// hoppes over, slik at brukeren leser de harde grunnene forst.
const RECEIPT_PLAN_GROUPS = [
  { kind: 'duplicate-file', icon: 'alertTri', tone: 'danger', key: 'inbox.planSameFile' },
  { kind: 'duplicate-invoice-number', icon: 'alertTri', tone: 'danger', key: 'inbox.planSameInvoiceNumber' },
  { kind: 'duplicate-content', icon: 'alertCircle', tone: 'warn', key: 'inbox.planSameContent' },
  { kind: 'needs-review', icon: 'eye', tone: '', key: 'inbox.planNeedsReview' },
  { kind: 'missing-data', icon: 'alertCircle', tone: 'warn', key: 'inbox.planMissingData' },
  { kind: 'missing', icon: 'alertCircle', tone: '', key: 'inbox.planMissing' },
];

// These controls belong to receipt routes; transaction details must not eagerly
// read them while registering the shared purchase pages.
Object.assign(window, { ReceiptBookingPlanDialog, ReceiptInboxWalkthrough });

function ReceiptPlanGroup({ icon, tone, title, items, children }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return <div className={'inbox-plan-group' + (tone ? ' ' + tone : '')}>
    <span className="inbox-plan-icon"><Icon name={icon} size={16}/></span>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div className="row between gap-10" style={{ alignItems: 'baseline' }}>
        <b className="t-sm">{title}</b>
        <button type="button" className="link-reset t-sm" onClick={() => setOpen((value) => !value)}>{open ? t('inbox.planHide') : t('inbox.planShow')}</button>
      </div>
      {open && <ul className="inbox-plan-list">{items.map((item) => <li key={item.id}>
        <b>{item.name}</b>{item.reason ? <span className="muted"> — {item.reason}</span> : null}
      </li>)}</ul>}
      {children}
    </div>
  </div>;
}

// Forhandsvisning av hva «Bokfor alle med SuperPlumma» kommer til aa gjore, og
// kvitteringen etterpaa. Samme dialog i begge tilstander: brukeren skal se
// resultatet der beslutningen ble tatt.
function ReceiptBookingPlanDialog({ ids, paymentAccount, onClose, onChanged }) {
  const backend = window.PlummeBackend;
  const [allowContent, setAllowContent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const plan = backend?.getReceiptInboxBookingPlan?.(ids, { duplicates: allowContent ? 'allowContent' : 'block' })
    || { book: [], skipped: [], counts: {} };
  const group = (kind) => plan.skipped.filter((item) => item.kind === kind);

  const run = async () => {
    setBusy(true);
    try {
      const outcome = await backend.bookReadyReceiptInboxEntries(plan.book.map((item) => item.id), {
        duplicates: 'allow',
        ...(paymentAccount ? { paymentAccount } : {}),
      });
      setResult(outcome);
      onChanged && onChanged(outcome);
    } catch (error) {
      toast(error?.message || t('inbox.bookFailedFallback'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const booked = result.booked || [];
    const skipped = [...(result.skipped || []), ...plan.skipped.filter((item) => !(result.skipped || []).some((other) => other.id === item.id))];
    const failed = result.failed || [];
    return <Modal open onClose={onClose} title={t('inbox.planResultTitle')} max={620}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
      <div className="col gap-12">
        <div className="t-h3">{t('inbox.planResultLead', { booked: booked.length, selected: ids.length })}</div>
        <ReceiptPlanGroup icon="check" tone="ok" title={t('inbox.planBooked', { count: booked.length })} items={booked}/>
        <ReceiptPlanGroup icon="eye" tone="" title={t('inbox.planSkipped', { count: skipped.length })} items={skipped}/>
        <ReceiptPlanGroup icon="alertTri" tone="danger" title={t('inbox.planFailed', { count: failed.length })} items={failed}/>
      </div>
    </Modal>;
  }

  const duplicateContent = group('duplicate-content');
  const noted = plan.book.filter((item) => (item.notes || []).length > 0);
  return <Modal open onClose={busy ? () => {} : onClose} title={t('inbox.planTitle')} max={620}
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="sparkle" disabled={busy || !plan.book.length} onClick={run}>
        {busy ? t('common.working') : t('inbox.planConfirm', { count: plan.book.length })}
      </Button>
    </React.Fragment>}>
    <div className="col gap-12">
      <div className="t-h3">{t('inbox.planLead', { count: plan.book.length, selected: ids.length })}</div>
      {!plan.book.length && <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('inbox.planNothing')}</div>}
      {/* Klare, men med noe vi har forvalgt. De sperres ikke — de skal bare ikke
          bokfores i blinde, og derfor telles de her ved siden av knappen. */}
      <ReceiptPlanGroup icon="alertCircle" tone="warn"
        title={t('inbox.planWithNotes', { count: noted.length })}
        items={noted.map((item) => ({ id: item.id, name: item.name, reason: item.notes.join(' ') }))}/>
      {RECEIPT_PLAN_GROUPS.map((definition) => <ReceiptPlanGroup key={definition.kind}
        icon={definition.icon} tone={definition.tone}
        title={t(definition.key, { count: group(definition.kind).length })}
        items={group(definition.kind)}>
        {definition.kind === 'duplicate-content' && duplicateContent.length > 0
          ? <label className="row gap-8 t-sm inbox-plan-check">
              <input type="checkbox" checked={allowContent} onChange={(event) => setAllowContent(event.target.checked)}/>
              <span>{t('inbox.planAllowContent')}</span>
            </label>
          : null}
      </ReceiptPlanGroup>)}
      {allowContent && <div className="t-sm inbox-plan-warn"><Icon name="alertTri" size={15}/>{t('inbox.planAllowContentWarn')}</div>}
    </div>
  </Modal>;
}

// Gjennomgang ett bilag om gangen. Koen er et oyeblikksbilde av utvalget: bilag
// forsvinner fra lista naar de bokfores, og indeksen ville ellers flyttet seg
// under fottene paa brukeren.
function ReceiptInboxWalkthrough({ entries, ids, paymentFor, onPaymentFor, onBook, onTrash, onSaved, onBookReadyNow, onClose, busy }) {
  // Rekkefolgen er listas, ikke klikkerekkefolgen: utvalget kan vaere plukket
  // hulter til bulter, og da mister man stedsansen i gjennomgangen.
  const [queue] = useState(() => {
    const wanted = new Set(Array.isArray(ids) ? ids : []);
    return (entries || []).filter((entry) => wanted.has(entry.id)).map((entry) => entry.id);
  });
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState('summary');
  const [tally, setTally] = useState({ booked: 0, skipped: 0, trashed: 0 });
  const byId = new Map((entries || []).map((entry) => [entry.id, entry]));
  const queued = queue.map((id) => byId.get(id)).filter(Boolean);
  const readyCount = queued.filter((entry) => entry.review?.status === 'ready-to-book').length;
  const reviewCount = queued.filter((entry) => entry.review?.status === 'needs-review').length;
  const duplicateCount = queued.filter((entry) => entry.duplicateUpload).length;
  const entry = byId.get(queue[index]);
  const count = (key, value) => setTally((current) => ({ ...current, [key]: current[key] + value }));

  const advance = () => {
    if (index + 1 >= queue.length) { setStep('done'); return; }
    setIndex(index + 1);
  };

  if (step === 'summary') {
    return <Modal open onClose={onClose} title={t('inbox.walkTitle')} max={560}
      footer={<React.Fragment>
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        {readyCount > 0 && <Button variant="secondary" icon="sparkle" onClick={onBookReadyNow} data-help={t('helpTopics.receiptReview.body')} data-help-title={t('helpTopics.receiptReview.title')}>{t('inbox.walkBookReady', { count: readyCount })}</Button>}
        <Button icon="arrowRight" disabled={!queued.length} onClick={() => setStep('entry')} data-help={t('helpTopics.receiptReview.body')} data-help-title={t('helpTopics.receiptReview.title')}>{t('inbox.walkStart', { count: queue.length })}</Button>
      </React.Fragment>}>
      <div className="col gap-10">
        <div className="t-h3">{t('inbox.walkLead', { count: queue.length })}</div>
        <div className="inbox-walk-stats">
          <div><b>{readyCount}</b><span>{t('inbox.walkReady')}</span></div>
          <div><b>{reviewCount}</b><span>{t('inbox.walkNeedsReview')}</span></div>
          <div><b>{duplicateCount}</b><span>{t('inbox.walkDuplicates')}</span></div>
        </div>
        <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('inbox.walkHint')}</div>
      </div>
    </Modal>;
  }

  if (step === 'done') {
    return <Modal open onClose={onClose} title={t('inbox.walkDoneTitle')} max={460}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
      <EmptyState acc="none" noVisual compact title={t('inbox.walkDoneLead', { count: queue.length })}
        text={t('inbox.walkDoneCounts', tally)}/>
    </Modal>;
  }

  const position = index + 1;
  const percent = Math.round((index / Math.max(1, queue.length)) * 100);
  return <Modal open onClose={onClose} max={1280} maxHeight="92vh"
    title={t('inbox.walkTitle')}
    titleRight={<span className="t-sm muted" style={{ fontWeight: 800 }}>{t('inbox.walkPosition', { index: position, total: queue.length })}</span>}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('inbox.walkPause')}</Button>
      <Button variant="secondary" icon="trash" disabled={!entry || busy === 'walk-trash'} onClick={async () => {
        if (!entry) return;
        if (await onTrash(entry.id)) { count('trashed', 1); advance(); }
      }} data-help={t('helpTopics.receiptReview.body')} data-help-title={t('helpTopics.receiptReview.title')}>{t('inbox.walkTrash')}</Button>
      <Button variant="secondary" iconRight="arrowRight" onClick={() => { count('skipped', 1); advance(); }} data-help={t('helpTopics.receiptReview.body')} data-help-title={t('helpTopics.receiptReview.title')}>{t('inbox.walkSkip')}</Button>
    </React.Fragment>}>
    <div className="col gap-12">
      <div className="inbox-walk-bar"><div style={{ width: percent + '%' }}/></div>
      {!entry
        ? <EmptyState acc="none" noVisual compact title={t('inbox.walkGone')} text={t('inbox.walkGoneText')}
            action={<Button onClick={advance} data-help={t('helpTopics.receiptReview.body')} data-help-title={t('helpTopics.receiptReview.title')}>{t('inbox.walkNext')}</Button>}/>
        : <React.Fragment>
          <div className="row between gap-10">
            <b className="t-h3" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{receiptUiName(entry)}</b>
            <span className="t-sm" style={{ fontWeight: 900, flex: 'none', color: entry.review?.status === 'ready-to-book' ? 'var(--success)' : 'var(--warning)' }}>{entry.review?.label || ''}</span>
          </div>
          {entry.duplicateUpload && <div className="t-sm inbox-plan-warn"><Icon name="alertTri" size={15}/>
            <span>{receiptDuplicateUploadText(entry.duplicateUpload)}
              {/* Navnet paa sosterbilaget gjor varselet handterbart: uten det maa
                  brukeren lete gjennom lista for aa se hva det er likt. */}
              {(() => {
                const names = (entry.duplicateUpload.ids || []).map((id) => byId.get(id)).filter(Boolean).map(receiptUiName);
                return names.length ? <span className="muted"> {t('inbox.walkDuplicateOf', { names: names.join(', ') })}</span> : null;
              })()}
            </span></div>}
          <ReceiptInboxExpanded key={entry.id} entry={entry} isDocs={false}
            paymentAccount={paymentFor(entry)}
            onPaymentAccount={(value) => onPaymentFor(entry, value)}
            onBook={async (account) => { if (await onBook(entry.id, account || paymentFor(entry))) { count('booked', 1); advance(); } }}
            onSaved={onSaved} busy={busy}/>
        </React.Fragment>}
    </div>
  </Modal>;
}

// Betalingskilde for mange bilag i en handling. Med nitti kortkjop i innboksen er
// dette forskjellen mellom to minutter og nitti apninger — og valget lagres paa
// bilaget, saa det overlever en oppdatering av sida.
function ReceiptPaymentSourceBulkDialog({ ids, onClose, onSaved }) {
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState(false);
  const count = (ids || []).length;
  const save = async () => {
    if (!account) { toast(t('inbox.bulkPaymentPick'), 'error'); return; }
    setBusy(true);
    try {
      const changed = await window.PlummeBackend.setReceiptInboxPaymentSource(ids, account);
      toast(t('inbox.bulkPaymentDone', { count: changed }), 'success');
      onSaved && onSaved();
    } catch (error) {
      toast(error?.message || t('inbox.setPaymentSourceFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open onClose={busy ? () => {} : onClose} title={t('inbox.bulkPaymentTitle')} max={520}
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" disabled={busy || !account} onClick={save}>{busy ? t('common.working') : t('inbox.bulkPaymentConfirm', { count })}</Button>
    </React.Fragment>}>
    <div className="col gap-12">
      <div className="t-sm" style={{ fontWeight: 700 }}>{t('inbox.bulkPaymentLead', { count })}</div>
      <Field label={t('inbox.bulkPaymentLabel')} hint={t('inbox.bulkPaymentHint')} help={t('helpTopics.privatePayment.body')} helpTitle={t('helpTopics.privatePayment.title')}>
        <ReceiptPaymentSelect value={account} onChange={setAccount}/>
      </Field>
    </div>
  </Modal>;
}
